import { t } from "@travily/i18n";
import type ListingsPhoto from "@travily/types/models/ListingsPhoto";
import { DeleteConfirmDialog } from "@ui/components/DeleteConfirmDialog";
import { EmptyState } from "@ui/components/EmptyState";
import { Field } from "@ui/components/Field";
import { Illustration } from "@ui/components/Illustration";
import { PhotoOrganizer } from "@ui/components/PhotoOrganizer";
import { StandardButton } from "@ui/components/StandardButton";
import { PhotoUploadModal, type UploadFileItem } from "@/components/PhotoUploadModal/PhotoUploadModal";
import type { ListingFieldCopy, ListingType } from "./types";

// i18n-tasks-use t('web.listings.fields.photos.product.heading')
// i18n-tasks-use t('web.listings.fields.photos.product.helper')
// i18n-tasks-use t('web.listings.fields.photos.product.empty_description')
// i18n-tasks-use t('web.listings.fields.photos.service.heading')
// i18n-tasks-use t('web.listings.fields.photos.service.helper')
// i18n-tasks-use t('web.listings.fields.photos.service.empty_description')

/**
 * The heading changes with state: an empty gallery asks for photos, a populated
 * one describes the minimum and how to reorder. Both flows render whichever
 * pair applies in their own chrome.
 */
export function photosFieldCopy({
  hasPhotos,
  listingType,
  minPhotos,
}: {
  hasPhotos: boolean;
  listingType: ListingType;
  minPhotos: number;
}): ListingFieldCopy {
  return hasPhotos
    ? {
        heading: t("web.listings.fields.photos.gallery_title", { min: minPhotos }),
        helper: t("web.listings.fields.photos.gallery_subtitle"),
      }
    : {
        heading: t(`web.listings.fields.photos.${listingType}.heading`),
        helper: t(`web.listings.fields.photos.${listingType}.helper`, { min: minPhotos }),
      };
}

export interface PhotosFieldProps {
  /** Whether all queued uploads have finished. */
  allUploadsCompleted: boolean;
  /** Field error for the photo set, or undefined when valid. */
  error?: string;
  /**
   * Whether the delete-confirmation dialog is showing. The confirmation trio
   * is optional: the creator wizard deletes against the live listing and
   * confirms first, the editor stages deletions until Save and skips it.
   */
  isDeleteModalOpen?: boolean;
  /** Whether an upload is in flight. */
  isUploading: boolean;
  /** Whether the upload modal is showing. */
  isUploadModalOpen: boolean;
  /** Which listing the field belongs to; selects the empty-state copy. */
  listingType: ListingType;
  /** Minimum photos the listing must have before it can be published. */
  minPhotos: number;
  /** Queue files for upload. */
  onAddFiles: (files: FileList) => void;
  /** Open the upload modal (empty-state CTA, gallery header, "add more"). */
  onAddPhotos: () => void;
  /** Commit the queued uploads. */
  onConfirmUpload: () => void;
  /** Confirm the pending deletion. */
  onConfirmDelete?: () => void;
  /** Request deletion of a photo, opening the confirmation when present. */
  onDeletePhoto: (photoId: string) => void;
  onDeleteModalOpenChange?: (isOpen: boolean) => void;
  /** Persist a new photo order. */
  onReorderPhotos: (orderedIds: string[]) => void;
  onRemoveUploadFile: (id: string) => void;
  onUploadModalOpenChange: (isOpen: boolean) => void;
  photos: ListingsPhoto[];
  uploadFiles: UploadFileItem[];
}

export function PhotosField({
  allUploadsCompleted,
  error,
  isDeleteModalOpen,
  isUploading,
  isUploadModalOpen,
  listingType,
  minPhotos,
  onAddFiles,
  onAddPhotos,
  onConfirmDelete,
  onConfirmUpload,
  onDeleteModalOpenChange,
  onDeletePhoto,
  onRemoveUploadFile,
  onReorderPhotos,
  onUploadModalOpenChange,
  photos,
  uploadFiles,
}: PhotosFieldProps) {
  const hasPhotos = photos.length > 0;

  return (
    <>
      {hasPhotos ? (
        <div className="w-full">
          <PhotoOrganizer
            aspectRatio="square"
            minPhotos={minPhotos}
            onAddMore={onAddPhotos}
            onDelete={onDeletePhoto}
            onReorder={onReorderPhotos}
            photos={photos}
          />
        </div>
      ) : (
        <EmptyState
          action={
            <StandardButton color="neutral" hierarchy="primary" size="44" onPress={onAddPhotos}>
              {t("web.listings.fields.photos.add_photos")}
            </StandardButton>
          }
          className="w-full"
          description={t(`web.listings.fields.photos.${listingType}.empty_description`)}
          illustration={<Illustration name="photos" />}
          title={t("web.listings.fields.photos.empty_title")}
        />
      )}

      {error ? (
        <Field>
          <Field.Error>{error}</Field.Error>
        </Field>
      ) : null}

      <PhotoUploadModal
        allCompleted={allUploadsCompleted}
        aspectRatio="square"
        files={uploadFiles}
        isOpen={isUploadModalOpen}
        isUploading={isUploading}
        onAddFiles={onAddFiles}
        onConfirm={onConfirmUpload}
        onOpenChange={onUploadModalOpenChange}
        onRemoveFile={onRemoveUploadFile}
      />

      {onConfirmDelete && onDeleteModalOpenChange ? (
        <DeleteConfirmDialog
          description={t("web.listings.fields.photos.delete_confirmation")}
          isPending={false}
          onConfirm={onConfirmDelete}
          isOpen={isDeleteModalOpen ?? false}
          onOpenChange={onDeleteModalOpenChange}
        />
      ) : null}
    </>
  );
}
